import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { NeighborContext } from '../../../api/app-types/neighbors.app';
import { Workplace } from '../../../api/app-types/workplace.app';
import { InstitutionExtended } from '../../../api/app-types/institution.app';
import { WORKPLACE_SERVICE } from '../../../api/services/workplace.api';
import { LOGGER } from '../../logging/log.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { LocalStorageService } from '../../storage/local-storage.service';
import { INSTITUTION_SERVICE } from '../../../api/services/institution.service';

export interface InstitutionAndWorkplace {
	institution: InstitutionExtended;
	workplace: Workplace;
}

const resolveInitialPageSize = (
	storage: LocalStorageService,
	auth: MsalService | null,
	preferenceKey: string,
	defaultPageSize: number,
	pageSizeOptions: number[],
) => {
	const userId = storage.resolveCurrentUserId(auth);
	if (!userId) return defaultPageSize;

	const saved = storage.getPageSize(userId, preferenceKey);
	return saved && pageSizeOptions.includes(saved) ? saved : defaultPageSize;
};

export const institutionWorkplacesResolver: ResolveFn<PaginatedResponse<Workplace>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(WORKPLACE_SERVICE);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });
	const id = Number(_route.paramMap.get('id')!);
	const initialRange = resolveInitialPageSize(
		storage,
		auth,
		'workplace-list',
		env.defaultPageSize,
		env.pageSizeOptions,
	);
	const response = await api.getAllByInstitutionId(id, 0, initialRange);
	return response;
};

export const institutionWorkplaceResolver: ResolveFn<Workplace> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(WORKPLACE_SERVICE);
	const institutionId = Number(_route.paramMap.get('id')!);
	const workplaceId = Number(_route.paramMap.get('branchId')!);
	const response = await api.getById(institutionId, workplaceId);
	return response;
};

export const institutionAndWorkplaceEditResolver: ResolveFn<InstitutionAndWorkplace | RedirectCommand> = async (
	route,
) => {
	const institutionApi = inject(INSTITUTION_SERVICE);
	const workplaceApi = inject(WORKPLACE_SERVICE);
	const router = inject(Router);

	const institutionId = Number(route.paramMap.get('id'));
	const workplaceId = Number(route.paramMap.get('branchId'));

	const [institution, workplace] = await Promise.all([
		institutionApi.getById(institutionId),
		workplaceApi.getById(institutionId, workplaceId),
	]);

	const isSchool = workplace.schoolNumber != null || (workplace.isMainInstitution && institution.schoolNumber != null);

	if (isSchool) {
		return new RedirectCommand(router.createUrlTree(['/desk/institution', institutionId, 'workplace', workplaceId]), {
			replaceUrl: true,
		});
	}

	return { institution, workplace };
};

export const allInstitutionWorkplacesResolver: ResolveFn<Workplace[]> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(WORKPLACE_SERVICE);
	const env = inject(ENVIRONMENT);
	const logger = inject(LOGGER);
	const institutionId = Number(_route.paramMap.get('id')!);
	logger.log(`Fetching all workplaces for institution ID: ${institutionId}`);
	const response = await api.getAllByInstitutionId(institutionId, 0, env.pagination.range); // Adjust offset and range as needed
	return response.entities;
};

export const institutionWorkplaceNeighborsResolver: ResolveFn<NeighborContext<Workplace>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(WORKPLACE_SERVICE);
	const institutionId = route.paramMap.get('id')!;
	const workplaceId = route.paramMap.get('branchId')!;
	return api.getWorkplaceNeighbors(institutionId, workplaceId);
};
